import { BlindPanelSide } from "building-types/casework/caseworkTypesEnum";
import { DIST_EPSILON, ZERO_EPSILON } from "geom/core";
import { CurveCurvePointIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import { polyLine2Copy, polyLine2Extend, polyLine2Vertex } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2Dist, vec2Normalize, vec2Perp, type Vec2 } from "geom/vec2";

/**
 * Whether a corner feature sits at the path *start*; host paths reverse when anchored from the far end.
 * An absent side pins to the path end without flipping, the asymmetry blind panels have always had.
 */
export function cornerFeatureAtPathStart(side: BlindPanelSide | undefined, paramFromEnd: boolean): boolean {
  return (side === BlindPanelSide.LEFT && paramFromEnd) || (side === BlindPanelSide.RIGHT && !paramFromEnd);
}

/** A straight cut taken across one front corner of a cabinet box — the `ANGLED` cabinet shape. */
export interface FrontCornerMiter {
  /**
   * How much of the front edge to leave standing, measured from the cabinet's far end. Absent, or
   * wider than the box, leaves the edge whole — which is no cut at all.
   */
  readonly frontFaceWidth: number | undefined;
  /** How much of the end edge to leave standing, measured from the back. See `frontFaceWidth`. */
  readonly endFaceWidth: number | undefined;
  /** Which end of the cabinet the corner is cut from — see `cornerFeatureAtPathStart`. */
  readonly side: BlindPanelSide | undefined;
  /** Mirrors the cut to the cabinet's other end — the shape's `reverse`, and a mirrored plan. */
  readonly reverse: boolean;
}

/** A 45° slice off the back corner across from the angled face — the `ANGLED` shape's `rearWidth`. */
export interface BackCornerMiter {
  /** How far the slice reaches along both edges — equal at 45°, so the face is `legLength * √2` wide. */
  readonly legLength: number;
  /** Which end of the cabinet the front cut takes; the slice takes the back corner at the other. */
  readonly side: BlindPanelSide | undefined;
  /** Mirrors the slice to the cabinet's other end — the shape's `reverse`, and a mirrored plan. */
  readonly reverse: boolean;
}

/** The two paths a shaped cabinet front yields — see `miterFrontPath`. */
export interface ShapedFront {
  /** The full front profile, closed back against the host path to build the solid. */
  readonly path: PolyLine2;
  /**
   * Where doors hang: the shaped face alone, leaving the remaining flat front bare. Spans the whole
   * front when the cut clamps away to nothing, since the box is then rectangular.
   */
  readonly doorPath: PolyLine2;
}

/** The narrowest angled face still worth doors, below which the door face takes whatever is left. */
const MIN_DOOR_FACE_WIDTH = 2;

/**
 * The unshaped result, for a cut or notch that leaves no corner. `doorPath` is a copy rather than
 * `path` itself, so a caller extending one path — as the blind panel does — cannot reach into the other.
 */
function unshapedFront(frontPath: PolyLine2): ShapedFront {
  return { path: frontPath, doorPath: polyLine2Copy(frontPath) };
}

/**
 * The angled face with both ends pulled in by `inset`, which centers the doors on it and stops them
 * short of the box's edges. Clamped, so a face too narrow to give the inset up still keeps a door.
 */
function insetDoorFace(doorFace: PolyLine2, inset: number): PolyLine2 {
  const width = vec2Dist(doorFace[0], doorFace[1], doorFace[2], doorFace[3]);
  const clamped = Math.min(inset, (width - MIN_DOOR_FACE_WIDTH) / 2);

  return clamped > 0 ? polyLine2Extend(doorFace, -clamped, -clamped) : doorFace;
}

/** One corner of a profile replaced by a straight cut, with that cut face on its own. */
interface CornerCut {
  readonly path: PolyLine2;
  readonly face: PolyLine2;
}

/**
 * The terminal corner of `path` replaced by a straight cut across the two edges meeting there — the
 * one along `path`, the one running back to `behindPath` — reaching as far down each as `legs` asks.
 *
 * Each leg is clamped to its own edge, and a corner with no room left is not cut at all: a profile
 * folding through itself extrudes open.
 */
function cutCorner(
  path: PolyLine2,
  behindPath: PolyLine2,
  atStart: boolean,
  legs: (alongEdge: number, behindEdge: number) => [number, number],
): CornerCut | undefined {
  if (path.length < 4 || behindPath.length < 4) {
    return undefined;
  }

  // The corner being cut, the vertex next to it along the path, and the vertex directly behind it.
  const corner = atStart ? 0 : path.length - 2;
  const neighbor = atStart ? 2 : path.length - 4;
  const behind = atStart ? 0 : behindPath.length - 2;

  const [cornerX, cornerY] = [path[corner], path[corner + 1]];
  const [neighborX, neighborY] = [path[neighbor], path[neighbor + 1]];
  const [behindX, behindY] = [behindPath[behind], behindPath[behind + 1]];

  const alongEdge = vec2Dist(cornerX, cornerY, neighborX, neighborY);
  const behindEdge = vec2Dist(cornerX, cornerY, behindX, behindY);
  const [requestedAlong, requestedBehind] = legs(alongEdge, behindEdge);
  const alongLeg = Math.min(requestedAlong, alongEdge - DIST_EPSILON);
  const behindLeg = Math.min(requestedBehind, behindEdge - DIST_EPSILON);

  if (alongLeg <= DIST_EPSILON || behindLeg <= DIST_EPSILON) {
    return undefined;
  }

  const [alongDirX, alongDirY] = vec2Normalize(neighborX - cornerX, neighborY - cornerY);
  const onAlong = [cornerX + alongDirX * alongLeg, cornerY + alongDirY * alongLeg];

  const [behindDirX, behindDirY] = vec2Normalize(behindX - cornerX, behindY - cornerY);
  const onBehind = [cornerX + behindDirX * behindLeg, cornerY + behindDirY * behindLeg];

  // Walk the cut from the end edge to the near edge so the path still runs start-to-end.
  const cut = polyLine2New(path.length + 2);
  if (atStart) {
    cut.set([...onBehind, ...onAlong], 0);
    cut.set(path.subarray(2), 4);
  } else {
    cut.set(path.subarray(0, corner), 0);
    cut.set([...onAlong, ...onBehind], corner);
  }

  // The two inserted points are the cut face itself; copied, so extending one path can't reach into
  // the other. A full-width cut collapses the edge beside it, leaving this as the whole face.
  const face = atStart ? cut.slice(0, 4) : cut.slice(cut.length - 4);

  return { path: cut, face };
}

/** How far a cut has to reach along an edge to leave `faceWidth` of it standing. */
function cutReachForFace(edgeLength: number, faceWidth: number | undefined): number {
  return faceWidth === undefined ? 0 : Math.max(0, edgeLength - faceWidth);
}

/**
 * The angled face the two flanking faces leave between them, given the edges they stand on — what
 * `miterFrontPath` cuts, ahead of a path to cut it from. Zero where they leave no corner.
 */
export function angledFaceWidth(
  frontEdgeLength: number,
  endEdgeLength: number,
  { frontFaceWidth, endFaceWidth }: Pick<FrontCornerMiter, "frontFaceWidth" | "endFaceWidth">,
): number {
  const frontLeg = cutReachForFace(frontEdgeLength, frontFaceWidth);
  const endLeg = cutReachForFace(endEdgeLength, endFaceWidth);

  return frontLeg > DIST_EPSILON && endLeg > DIST_EPSILON ? Math.hypot(frontLeg, endLeg) : 0;
}

/**
 * The cabinet front with one corner cut away, its terminal vertex replaced by the two cut points. The
 * face widths leave the edges standing, so the face the doors hang on tracks the box's size as well.
 */
export function miterFrontPath(
  frontPath: PolyLine2,
  backPath: PolyLine2,
  { frontFaceWidth, endFaceWidth, side, reverse }: FrontCornerMiter,
  paramFromEnd: boolean,
  /** Bare face left at each end of the cut, between the box's edge and the door path. */
  doorEndInset: number = 0,
): ShapedFront {
  const atStart = cornerFeatureAtPathStart(side, paramFromEnd) !== reverse;
  const cut = cutCorner(frontPath, backPath, atStart, (alongEdge, behindEdge) => [
    cutReachForFace(alongEdge, frontFaceWidth),
    cutReachForFace(behindEdge, endFaceWidth),
  ]);

  if (cut === undefined) {
    return unshapedFront(frontPath);
  }

  return { path: cut.path, doorPath: insetDoorFace(cut.face, doorEndInset) };
}

/**
 * The cabinet's host path with one back corner sliced off at 45°, taken directly across from the
 * angled face: the box's far corner, which is the end the front cut does *not* take.
 *
 * The two cuts share no edge, so each stays a chord of a convex profile and they cannot meet however
 * deep they run — all that bounds this one is the two edges it sits on.
 */
export function miterBackPath(
  backPath: PolyLine2,
  frontPath: PolyLine2,
  { legLength, side, reverse }: BackCornerMiter,
  paramFromEnd: boolean,
): PolyLine2 {
  if (!(legLength > 0)) {
    return backPath;
  }

  const atStart = cornerFeatureAtPathStart(side, paramFromEnd) === reverse;
  const cut = cutCorner(backPath, frontPath, atStart, (alongEdge, behindEdge) => {
    // Equal legs are what holds the slice at 45°, so the shorter of the two edges shortens both.
    const leg = Math.min(legLength, alongEdge, behindEdge);
    return [leg, leg];
  });

  return cut?.path ?? backPath;
}

/** A right-angle notch taken out of one front corner of a cabinet box — the `L_SHAPED` cabinet shape. */
export interface FrontCornerNotch {
  /** How far the notch reaches along the front edge, from the corner being cut. */
  readonly frontNotchLength: number;
  /** How far the notch reaches along the end edge, from the same corner. */
  readonly endNotchLength: number;
  /** Which end of the cabinet the notch is cut from — see `cornerFeatureAtPathStart`. */
  readonly side: BlindPanelSide | undefined;
  /** Mirrors the notch to the cabinet's other end — the shape's `reverse`, and a mirrored plan. */
  readonly reverse: boolean;
}

/**
 * The cabinet front with one corner notched square — `miterFrontPath`'s mechanism, stepped
 * rather than mitered. Doors hang on the notch's two faces; either leg clamping away leaves it whole.
 */
export function notchFrontPath(
  frontPath: PolyLine2,
  backPath: PolyLine2,
  { frontNotchLength, endNotchLength, side, reverse }: FrontCornerNotch,
  paramFromEnd: boolean,
): ShapedFront {
  if (frontNotchLength <= 0 || endNotchLength <= 0 || frontPath.length < 4 || backPath.length < 4) {
    return unshapedFront(frontPath);
  }

  const atStart = cornerFeatureAtPathStart(side, paramFromEnd) !== reverse;

  // The corner being cut, the front vertex next to it, and the back vertex directly behind it.
  // Negative indices count back from the path's end.
  const cornerVertex = atStart ? 0 : -1;
  const neighborVertex = atStart ? 1 : -2;
  const behindVertex = atStart ? 0 : -1;

  // Read straight into locals: `polyLine2Vertex` hands back one shared scratch vector.
  const [cornerX, cornerY] = polyLine2Vertex(frontPath, cornerVertex);
  const [neighborX, neighborY] = polyLine2Vertex(frontPath, neighborVertex);
  const [behindX, behindY] = polyLine2Vertex(backPath, behindVertex);

  // `alongFront` and `alongEnd` are the lengths of the two sides meeting at the corner. Clamping each
  // leg to its own side stops the notch cutting past the end of the side it cuts, which folds the
  // profile through itself and extrudes open.
  const alongFront = vec2Dist(cornerX, cornerY, neighborX, neighborY);
  const alongEnd = vec2Dist(cornerX, cornerY, behindX, behindY);
  const frontCut = Math.min(frontNotchLength, alongFront - DIST_EPSILON);
  const endCut = Math.min(endNotchLength, alongEnd - DIST_EPSILON);

  if (frontCut <= DIST_EPSILON || endCut <= DIST_EPSILON) {
    return unshapedFront(frontPath);
  }

  const [frontDirX, frontDirY] = vec2Normalize(neighborX - cornerX, neighborY - cornerY);
  const [endDirX, endDirY] = vec2Normalize(behindX - cornerX, behindY - cornerY);

  const frontCorner = [cornerX + frontDirX * frontCut, cornerY + frontDirY * frontCut];
  const endCorner = [cornerX + endDirX * endCut, cornerY + endDirY * endCut];
  // The notch's inner corner: as far along the end direction as `endCorner`, then across to the front leg.
  const stepCorner = [endCorner[0] + frontDirX * frontCut, endCorner[1] + frontDirY * frontCut];

  // The notch's two faces are exactly where doors hang. Walked from the end edge to the front edge so
  // that copying them into the full front below leaves that path still running start-to-end.
  const doorPath = polyLine2New(
    atStart ? [...endCorner, ...stepCorner, ...frontCorner] : [...frontCorner, ...stepCorner, ...endCorner],
  );

  // A copy, so a caller extending one path — as the blind panel does — cannot reach into the other.
  const notched = polyLine2New(frontPath.length + 4);
  if (atStart) {
    notched.set(doorPath, 0);
    notched.set(frontPath.subarray(2), 6);
  } else {
    const tail = frontPath.length - 2;
    notched.set(frontPath.subarray(0, tail), 0);
    notched.set(doorPath, tail);
  }

  return { path: notched, doorPath };
}

/** An edge's unit normal, 90° clockwise from its direction — the same convention as `line2Normal`. */
function edgeNormal(x0: number, y0: number, x1: number, y1: number): Vec2 {
  const [dx, dy] = vec2Normalize(x1 - x0, y1 - y0);
  // A fresh pair rather than the scratch vector `vec2Perp` hands back, so a caller may hold it.
  const [perpX, perpY] = vec2Perp(dx, dy);
  return [perpX, perpY];
}

/** Appends a point unless it repeats the one already there, which the degenerate joins below produce. */
function pushDistinct(out: number[], x: number, y: number) {
  if (out.length < 2 || vec2Dist(out[out.length - 2], out[out.length - 1], x, y) > DIST_EPSILON) {
    out.push(x, y);
  }
}

/**
 * How far a front face can be set back before the corner it shares with the end face beside it lands
 * behind the box, less an epsilon that keeps the corner in front of the host path rather than exactly
 * on it. `hostX, hostY` is the end edge's far point, on the host path that `hostDir` runs along.
 */
function setbackLimit(
  cornerX: number,
  cornerY: number,
  hostX: number,
  hostY: number,
  hostDirX: number,
  hostDirY: number,
  normalX: number,
  normalY: number,
): number {
  // How far in front of the host path the corner stands, and how fast the setback eats into that.
  const ahead = hostDirX * (cornerY - hostY) - hostDirY * (cornerX - hostX);
  const sign = ahead < 0 ? -1 : 1;
  const closing = -sign * (hostDirX * normalY - hostDirY * normalX);

  // A face leaning away from the back never reaches it, however far it moves.
  return closing > ZERO_EPSILON ? Math.max(0, sign * ahead - DIST_EPSILON) / closing : Infinity;
}

/**
 * The cabinet front moved by `offset` — back for a toe kick's recess, forward for a countertop's
 * overhang. Each front face moves on its own normal and miters into the next; the end faces stay put.
 *
 * A setback that would otherwise cut into an end face returns square to the front instead, so the end
 * faces keep the width the carcass gives them and each layer meets the cabinets beside it flush.
 *
 * A face the setback would flatten onto the host path along its whole length keeps its place instead:
 * a shallow cabinet, or the short leg of a shaped one, carries no recess there. Setting it back as far
 * as it could go would leave nothing to extrude, and that reaches the plan loops, the floor cut and
 * every face downstream as degenerate geometry rather than as the missing recess it stands in for.
 */
export function offsetCabinetFront(frontPath: PolyLine2, hostPath: PolyLine2, offset: number): PolyLine2 {
  const [hostStartX, hostStartY] = [hostPath[0], hostPath[1]];
  const [hostEndX, hostEndY] = [hostPath[hostPath.length - 2], hostPath[hostPath.length - 1]];

  // The front plus the two end edges it closes against, so each front face meets them like any other.
  const points = [hostStartX, hostStartY, ...frontPath, hostEndX, hostEndY];
  const edgeCount = points.length / 2 - 1;
  const firstFrontEdge = 1;
  const lastFrontEdge = edgeCount - 2;
  const isFrontEdge = (edge: number) => edge >= firstFrontEdge && edge <= lastFrontEdge;

  // Which side of the host path the front sits on, from the point standing furthest off it so that a
  // cut bringing one end of the front back onto the host cannot decide it. A negative `depth` puts the
  // front on the inset side, which flips the box's winding and every inward normal with it.
  const [hostNormalX, hostNormalY] = edgeNormal(hostStartX, hostStartY, hostPath[2], hostPath[3]);
  let furthest = 0;
  for (let i = 0; i < frontPath.length; i += 2) {
    const side = hostNormalX * (frontPath[i] - hostStartX) + hostNormalY * (frontPath[i + 1] - hostStartY);
    if (Math.abs(side) > Math.abs(furthest)) {
      furthest = side;
    }
  }
  const insetSign = furthest > 0 ? -1 : 1;

  /** The unit normal an edge moves along for a positive `offset` — into the box, either way round. */
  const insetNormal = (x0: number, y0: number, x1: number, y1: number): Vec2 => {
    const [normalX, normalY] = edgeNormal(x0, y0, x1, y1);
    return [insetSign * normalX, insetSign * normalY];
  };

  /**
   * How much box a point has in front of the host path — the depth there is left to extrude. Measured
   * off the host's first edge alone, so a host that turns is still one straight line to the front.
   */
  const depthInFront = (x: number, y: number): number =>
    -insetSign * (hostNormalX * (x - hostStartX) + hostNormalY * (y - hostStartY));

  // The host path's own direction at either end, which no setback may carry a corner past.
  const [hostStartDirX, hostStartDirY] = vec2Normalize(hostPath[2] - hostStartX, hostPath[3] - hostStartY);
  const [hostEndDirX, hostEndDirY] = vec2Normalize(
    hostEndX - hostPath[hostPath.length - 4],
    hostEndY - hostPath[hostPath.length - 3],
  );

  // Each front face on its own normal; the end faces stay put, so their segments are their own.
  const starts: number[] = [];
  const ends: number[] = [];
  for (let edge = 0; edge < edgeCount; edge++) {
    const [x0, y0, x1, y1] = [points[2 * edge], points[2 * edge + 1], points[2 * edge + 2], points[2 * edge + 3]];

    if (!isFrontEdge(edge)) {
      starts.push(x0, y0);
      ends.push(x1, y1);
      continue;
    }

    const [normalX, normalY] = insetNormal(x0, y0, x1, y1);
    let moveDist = offset;

    // Only a setback can run out of box to move into; an overhang moves the other way.
    if (offset > 0) {
      // A setback at least as deep as the deepest end of the face leaves nothing of it to extrude, so
      // that face keeps its place. A shallower one may still be clamped below: bringing a single corner
      // onto the host path costs the face nothing, because its other end keeps the box's depth.
      const faceDepth = Math.max(depthInFront(x0, y0), depthInFront(x1, y1));
      if (offset >= faceDepth - DIST_EPSILON) {
        moveDist = 0;
      } else {
        // Each end face's far point is the host path's own end, which its corner may not pass. A lone
        // front face shares a corner with both, so both branches run.
        if (edge === firstFrontEdge) {
          const limit = setbackLimit(x0, y0, hostStartX, hostStartY, hostStartDirX, hostStartDirY, normalX, normalY);
          moveDist = Math.min(moveDist, limit);
        }
        if (edge === lastFrontEdge) {
          const limit = setbackLimit(x1, y1, hostEndX, hostEndY, hostEndDirX, hostEndDirY, normalX, normalY);
          moveDist = Math.min(moveDist, limit);
        }
      }
    }

    starts.push(x0 + normalX * moveDist, y0 + normalY * moveDist);
    ends.push(x1 + normalX * moveDist, y1 + normalY * moveDist);
  }

  // One joint per interior vertex: joint `j` sits at vertex `j`, between the faces `j - 1` and `j`.
  const out: number[] = [];
  for (let joint = 1; joint < edgeCount; joint++) {
    const [jointX, jointY] = [points[2 * joint], points[2 * joint + 1]];
    const [prevEndX, prevEndY] = [ends[2 * joint - 2], ends[2 * joint - 1]];
    const [nextStartX, nextStartY] = [starts[2 * joint], starts[2 * joint + 1]];
    const prevIsFront = isFrontEdge(joint - 1);
    const nextIsFront = isFrontEdge(joint);
    let miterCutsIntoEndFace = false;

    if (prevIsFront !== nextIsFront) {
      // The end face left in place, and the point at which the front beside it now stops.
      const endFaceEdge = prevIsFront ? joint : joint - 1;
      const [endFaceNormalX, endFaceNormalY] = insetNormal(
        points[2 * endFaceEdge],
        points[2 * endFaceEdge + 1],
        points[2 * endFaceEdge + 2],
        points[2 * endFaceEdge + 3],
      );
      const [stopX, stopY] = prevIsFront ? [prevEndX, prevEndY] : [nextStartX, nextStartY];

      // A setback that has moved in past the end face would eat into it, so it returns square at its
      // own edge instead. An overhang mitering out only makes that face longer, which is no loss.
      miterCutsIntoEndFace = endFaceNormalX * (stopX - jointX) + endFaceNormalY * (stopY - jointY) > DIST_EPSILON;
    }

    // Both faces as infinite lines (`restrictToLineBounds` false), each written outwards from the
    // joint — so the previous face running backwards from its own end is deliberate. Do not reorder.
    const corner = miterCutsIntoEndFace
      ? null
      : intersectLine2Line2(
          prevEndX,
          prevEndY,
          starts[2 * joint - 2],
          starts[2 * joint - 1],
          nextStartX,
          nextStartY,
          ends[2 * joint],
          ends[2 * joint + 1],
          ZERO_EPSILON,
          0,
          false,
        );

    if (corner instanceof CurveCurvePointIntersection) {
      pushDistinct(out, corner.x, corner.y);
      continue;
    }

    // Square return: out to where the previous face stops, through the corner the carcass has, then
    // on to where the next face starts. `pushDistinct` drops whichever of the three coincide.
    pushDistinct(out, prevEndX, prevEndY);
    pushDistinct(out, jointX, jointY);
    pushDistinct(out, nextStartX, nextStartY);
  }

  return polyLine2New(out);
}
